import { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRight, Languages, Maximize2, Minimize2 } from "lucide-react";

const PRESENT = { id: ["Mode Presentasi", "Keluar presentasi"], en: ["Presentation mode", "Exit presentation"], jp: ["プレゼンモード", "プレゼン終了"] };

function PresentButton({ lang }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    document.body.classList.toggle("present-mode", on);
    const onKey = (e) => e.key === "Escape" && setOn(false);
    const onFs = () => !document.fullscreenElement && setOn(false);
    window.addEventListener("keydown", onKey); document.addEventListener("fullscreenchange", onFs);
    return () => { document.body.classList.remove("present-mode"); window.removeEventListener("keydown", onKey); document.removeEventListener("fullscreenchange", onFs); };
  }, [on]);
  const toggle = () => {
    if (!on) document.documentElement.requestFullscreen?.().catch(() => {}); else if (document.fullscreenElement) document.exitFullscreen?.();
    setOn(!on);
  };
  const Icon = on ? Minimize2 : Maximize2;
  return <button type="button" onClick={toggle} data-testid="showcase-present-toggle" data-on={on ? "1" : "0"} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${on ? "bg-slate-900 text-white" : "bg-[color:var(--brand-blue)] text-white hover:bg-[color:var(--brand-blue-dark)]"}`}><Icon className="h-3.5 w-3.5" />{(PRESENT[lang] || PRESENT.id)[on ? 1 : 0]}</button>;
}

const C = {
  id: {
    title: "Keunggulan Sistem YLMS", sub: "Ringkasan untuk mitra & perusahaan penerima di Jepang",
    flow: "Alur: Siswa → Guru → Program → Jadwal 19 hari → Penilaian harian → Pertumbuhan siswa",
    strong: "Strong Point", improve: "Improvement Point", mascotStrong: "LUAR BIASA!", mascotImprove: "BERPIKIR…",
    cta: "Ganti bahasa di kanan atas untuk presentasi", open: "Buka",
    strongs: [
      ["Pertumbuhan 3 dimensi", "Setiap siswa dipantau: lebih pintar (Bahasa Jepang), lebih terampil (Skill/FMD), lebih baik sikapnya (Etika) — grafik & panah tren.", "/students"],
      ["Siklus 19 hari terstandar", "19 hari kerja × 8 JP = 152 JP per kelas (PCL/N5/N4/N3) dengan matriks alokasi yang dapat diubah pimpinan.", "/planning"],
      ["Penilaian mengikuti jadwal", "Guru menilai persis sesuai sesi hari itu; saklar ON/OFF per sub-mapel (会話・聴解・言葉/漢字/文法・読解).", "/assessment"],
      ["Import nilai dengan AI", "Excel nilai dibaca otomatis oleh AI, dipetakan ke siswa & mapel, lalu ditinjau sebelum disimpan.", "/assessment"],
      ["Sertifikat & laporan siap perusahaan", "Laporan siswa 7 area, sertifikat sementara otomatis, alumni & status Free/Selected terpantau.", "/reports"],
      ["Mulai rombel dalam 5 langkah", "Wizard: pilih siswa, guru, program, tanggal mulai → jadwal 19 hari dibuat otomatis.", "/setup"],
    ],
    improves: [
      ["Terjemahan penuh ke Bahasa Jepang", "Menu dan halaman utama sudah 3 bahasa; detail form & laporan PDF akan menyusul."],
      ["Aplikasi mobile untuk siswa", "Saat ini responsif di browser; aplikasi native untuk absensi QR & belajar mandiri direncanakan."],
      ["Integrasi dengan perusahaan penerima", "Portal mitra untuk melihat progres kandidat secara langsung (read-only)."],
      ["Analitik prediktif", "Prediksi kelulusan JLPT & kesiapan kerja dari tren nilai harian."],
    ],
  },
  en: {
    title: "YLMS System Highlights", sub: "Summary for partners & receiving companies in Japan",
    flow: "Flow: Students → Teachers → Program → 19-day schedule → Daily assessment → Student growth",
    strong: "Strong Points", improve: "Improvement Points", mascotStrong: "EXCELLENT!", mascotImprove: "THINKING…",
    cta: "Switch language at the top-right for presentations", open: "Open",
    strongs: [
      ["3-dimension growth", "Each student tracked: smarter (Japanese), more skilled (Skill/FMD), better attitude (Ethics) — charts & trend arrows.", "/students"],
      ["Standardized 19-day cycle", "19 workdays × 8 periods = 152 periods per class (PCL/N5/N4/N3) with an editable allocation matrix.", "/planning"],
      ["Assessment follows the schedule", "Teachers grade exactly today's sessions; ON/OFF switch per Japanese sub-subject.", "/assessment"],
      ["AI score import", "Excel score sheets are read by AI, mapped to students & subjects, then reviewed before saving.", "/assessment"],
      ["Company-ready certificates & reports", "7-area student report, automatic provisional certificates, alumni & Free/Selected monitoring.", "/reports"],
      ["Start a class group in 5 steps", "Wizard: students, teachers, program, start date → 19-day schedule generated automatically.", "/setup"],
    ],
    improves: [
      ["Full Japanese translation", "Menus & main pages are trilingual; form details & PDF reports to follow."],
      ["Mobile app for students", "Responsive today; native app for QR attendance & self-study planned."],
      ["Receiving-company integration", "Partner portal to view candidate progress directly (read-only)."],
      ["Predictive analytics", "Predict JLPT pass & job readiness from daily score trends."],
    ],
  },
  jp: {
    title: "YLMSシステムの特長", sub: "日本の受入企業・パートナー様向けサマリー",
    flow: "流れ：学生 → 教員 → プログラム → 19日間スケジュール → 日次評価 → 学生の成長",
    strong: "強み（Strong Point）", improve: "改善点（Improvement Point）", mascotStrong: "すばらしい！", mascotImprove: "検討中…",
    cta: "右上で言語を切り替えてプレゼンできます", open: "開く",
    strongs: [
      ["3つの成長軸", "学生一人ひとりを「より賢く（日本語）」「より上手に（技能・FMD）」「より良い態度（倫理）」で可視化。グラフと矢印で傾向表示。", "/students"],
      ["標準化された19日サイクル", "19営業日 × 8コマ = 152コマ／クラス（PCL・N5・N4・N3）。配分マトリクスは管理者が変更可能。", "/planning"],
      ["スケジュール連動の評価", "教員は当日のセッション通りに評価。会話・聴解・言葉/漢字/文法・読解ごとにON/OFF切替。", "/assessment"],
      ["AIによる成績インポート", "Excelの成績表をAIが読み取り、学生と科目に自動マッピング。保存前に確認。", "/assessment"],
      ["企業向け証明書・レポート", "7領域の学生レポート、仮証明書の自動発行、卒業生とFree/Selected状況の管理。", "/reports"],
      ["5ステップでクラス開始", "ウィザード：学生・教員・プログラム・開始日 → 19日間のスケジュールを自動生成。", "/setup"],
    ],
    improves: [
      ["日本語への完全翻訳", "メニューと主要画面は3言語対応済み。フォーム詳細とPDFレポートは順次対応。"],
      ["学生向けモバイルアプリ", "現在はブラウザ対応。QR出席・自習用のネイティブアプリを計画中。"],
      ["受入企業との連携", "候補者の進捗を直接閲覧できるパートナーポータル（閲覧専用）。"],
      ["予測分析", "日次成績の傾向からJLPT合格と就労準備度を予測。"],
    ],
  },
};

const MascotCard = ({ img, label, tone }) => (
  <div className={`relative overflow-hidden rounded-2xl p-4 flex items-center gap-4 ${tone}`} data-testid={`showcase-mascot-${img}`}>
    <img src={`/mascot/${img}.png`} alt="Yutaka mascot" className="h-24 w-24 object-contain drop-shadow-md oreo-dance" />
    <p className="font-heading font-extrabold text-lg text-white drop-shadow">{label}</p>
  </div>
);

export default function Showcase() {
  const { lang } = useI18n();
  const c = C[lang] || C.id;
  return (
    <div className="space-y-8 max-w-6xl" data-testid="showcase-page">
      <header className="flex flex-col sm:flex-row sm:items-end gap-4">
        <img src="/mascot/semangat.png" alt="Yutaka" className="h-28 w-28 object-contain oreo-dance" data-testid="showcase-hero-mascot" />
        <div className="flex-1">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white" data-testid="showcase-title">{c.title}</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{c.sub}</p>
          <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-sky-50 text-[color:var(--brand-blue)] px-3 py-1 text-xs font-semibold dark:bg-slate-800">{c.flow}</p>
        </div>
        <div className="flex flex-col items-end gap-2"><PresentButton lang={lang} /><p className="inline-flex items-center gap-1.5 text-xs text-slate-500"><Languages className="h-3.5 w-3.5" />{c.cta}</p></div>
      </header>

      <section className="space-y-4">
        <MascotCard img="luarbiasa" label={c.mascotStrong} tone="bg-[color:var(--brand-blue)]" />
        <h2 className="font-heading text-lg font-bold text-slate-900 dark:text-white">{c.strong}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {c.strongs.map(([h, d, to], i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-700 shadow-sm p-5 flex flex-col gap-2 hover:-translate-y-0.5 transition-transform" data-testid={`showcase-strong-${i}`}>
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              <h3 className="font-semibold text-slate-900 dark:text-white">{h}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 flex-1">{d}</p>
              <Link to={to} className="text-xs font-semibold text-[color:var(--brand-blue)] inline-flex items-center gap-1">{c.open} <ArrowUpRight className="h-3 w-3" /></Link>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <MascotCard img="berpikir" label={c.mascotImprove} tone="bg-orange-500" />
        <h2 className="font-heading text-lg font-bold text-slate-900 dark:text-white">{c.improve}</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {c.improves.map(([h, d], i) => (
            <div key={i} className="rounded-xl border border-dashed border-orange-300 bg-orange-50/40 dark:bg-slate-900 p-5 flex gap-3" data-testid={`showcase-improve-${i}`}>
              <ArrowUpRight className="h-5 w-5 text-orange-500 shrink-0" />
              <div><h3 className="font-semibold text-slate-900 dark:text-white">{h}</h3><p className="text-sm text-slate-600 dark:text-slate-300">{d}</p></div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
